import React, { useEffect, useMemo, useState } from 'react';
import L from 'leaflet';
import { GeoJSON, ScaleControl, ZoomControl, useMap } from 'react-leaflet';
import { SatelliteTileLayer, TILE_MODES } from 'components/Map/SatelliteTileLayer';
import { useGetSurveyCoverageQuery, useGetSurveyTracksQuery } from 'state';
import { useGetSurveyStonesQuery } from 'state';
import { wktToGeoJson, coveredFeatures, stonesInList } from '../../fields.utils';
import { RECORD_BLUE, STONE_CLASSES, NEUTRAL_STONE_CLASS } from '../../fields.constants';
import { StoneLayer } from './StoneLayer';
import {
  MapHolder,
  StyledMapContainer,
  Toolbar,
  ToolbarButton,
  Banner,
  Legend,
  LegendRow,
  Swatch,
  Dot
} from './FieldMap.styles';

const DEFAULT_CENTER = [49.8, -98.3];
const DEFAULT_ZOOM = 10;

const BOUNDARY_STYLE = { color: '#F2C500', weight: 2.6, fill: false, interactive: false };
const RECORD_BOUNDARY_STYLE = { ...BOUNDARY_STYLE, color: RECORD_BLUE };
const COVERAGE_STYLE = {
  weight: 0,
  fillColor: '#FFFFFF',
  fillOpacity: 0.56,
  interactive: false
};
const PATH_STYLE = {
  color: '#2154E8',
  weight: 0.8,
  opacity: 0.3,
  interactive: false
};

const FitToField = ({ boundary }) => {
  const map = useMap();

  useEffect(() => {
    if (!boundary) return;
    map.invalidateSize();
    map.fitBounds(L.geoJSON(boundary).getBounds().pad(0.1));
  }, [boundary, map]);

  return null;
};

export const FieldMap = ({
  boundaryWkt,
  surveyId,
  isRecord,
  isPreview,
  listStoneIds,
  banner,
  selectedStoneId,
  onSelectStone
}) => {
  const [showCoverage, setShowCoverage] = useState(true);
  const [showPaths, setShowPaths] = useState(false);
  const [showStones, setShowStones] = useState(true);

  const boundary = useMemo(() => wktToGeoJson(boundaryWkt), [boundaryWkt]);

  const { currentData: coverage, isError: isCoverageError } = useGetSurveyCoverageQuery(
    surveyId,
    { skip: !surveyId }
  );
  const { currentData: tracks, isError: isTracksError } = useGetSurveyTracksQuery(
    surveyId,
    { skip: !surveyId || !showPaths }
  );

  const { currentData: stones, isError: isStonesError } = useGetSurveyStonesQuery(
    surveyId,
    {
      skip: !surveyId || !showStones
    }
  );
  const shownStones = useMemo(
    () => stonesInList(stones, isPreview ? listStoneIds : null),
    [stones, isPreview, listStoneIds]
  );
  const exportedIds = useMemo(() => {
    const features = stones?.features ?? [];
    if (!isRecord || !features.some(({ properties }) => 'exported' in properties))
      return null;
    return new Set(
      features
        .filter(({ properties }) => properties.exported)
        .map(({ properties }) => properties.id)
    );
  }, [isRecord, stones]);
  const hasUnknownSize = shownStones?.features.some(
    feature => !feature.properties.size_class
  );

  const message = isCoverageError
    ? 'Failed to load coverage.'
    : showPaths && isTracksError
    ? 'Failed to load machine paths.'
    : showStones && isStonesError
    ? 'Failed to load stones.'
    : null;

  return (
    <MapHolder data-testid='map-area' $hasBanner={Boolean(banner)}>
      {banner}
      <StyledMapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        zoomControl={false}
      >
        <SatelliteTileLayer mode={TILE_MODES.SATELLITE} />
        <ZoomControl position='topright' />
        <ScaleControl imperial={false} />
        <FitToField boundary={boundary} />
        {showCoverage && coverage && (
          <GeoJSON
            key={`coverage-${surveyId}-${coverage.computed_at}`}
            data={coveredFeatures(coverage)}
            style={COVERAGE_STYLE}
          />
        )}
        {showPaths && tracks && (
          <GeoJSON key={`tracks-${surveyId}`} data={tracks} style={PATH_STYLE} />
        )}
        {showStones && shownStones && shownStones.features.length > 0 && (
          <StoneLayer
            stones={shownStones}
            listedIds={isRecord ? exportedIds : listStoneIds}
            selectedId={selectedStoneId}
            onSelect={onSelectStone}
          />
        )}
        {boundary && (
          <GeoJSON
            key={`${boundaryWkt}-${isRecord || isPreview}`}
            data={boundary}
            style={isRecord || isPreview ? RECORD_BOUNDARY_STYLE : BOUNDARY_STYLE}
          />
        )}
      </StyledMapContainer>

      <Toolbar $hasBanner={Boolean(banner)}>
        <ToolbarButton
          type='button'
          aria-pressed={showCoverage}
          onClick={() => setShowCoverage(value => !value)}
        >
          Ground imaged
        </ToolbarButton>
        <ToolbarButton
          type='button'
          aria-pressed={showPaths}
          onClick={() => setShowPaths(value => !value)}
        >
          Machine paths
        </ToolbarButton>
        <ToolbarButton
          type='button'
          aria-pressed={showStones}
          onClick={() => setShowStones(value => !value)}
        >
          Stones
        </ToolbarButton>
      </Toolbar>

      {message ? (
        <Banner>{message}</Banner>
      ) : (
        (showCoverage || showStones) && (
          <Legend>
            {showCoverage && (
              <LegendRow>
                <Swatch />
                Ground imaged in this survey
              </LegendRow>
            )}
            {showStones &&
              STONE_CLASSES.map(({ key, label, color }) => (
                <LegendRow key={key}>
                  <Dot $color={color} />
                  {label}
                </LegendRow>
              ))}
            {showStones && hasUnknownSize && (
              <LegendRow>
                <Dot $color={NEUTRAL_STONE_CLASS.color} />
                {NEUTRAL_STONE_CLASS.label}
              </LegendRow>
            )}
          </Legend>
        )
      )}
    </MapHolder>
  );
};
